"use client";

import { c as _c } from "react/compiler-runtime";
import { type UIMessage, useChat } from "@ai-sdk/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultChatTransport, getToolName, isTextUIPart, isToolUIPart } from "ai";
import { BrainCircuit, Meh, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { api } from "@/app/_trpc/client";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import AvatarImage from "@/layout/Avatar";
import RichInput from "@/layout/RichInput";
import { cn } from "@/libs/shadui";
import { showMutationToast } from "@/libs/toast";
import { useUserData } from "@/utils/UserContext";
import { type ChatMessageSchema, chatMessageSchema } from "@/validators/chat";
interface ToolCall<NAME extends string, ARGS> {
  toolCallId: string;
  toolName: NAME;
  args: ARGS;
}
export interface ChatBoxProps {
  className?: string;
  position?: "fixed" | "relative";
  onClose?: () => void;
  showCloseButton?: boolean;
  showHeader?: boolean;
  showFeedback?: boolean;
  autoFocus?: boolean;
  aiProps: {
    apiEndpoint: string;
    systemMessage?: string;
  };
  onToolCall: (toolCall: ToolCall<string, unknown>) => void;
}
const getMessageText = (message: UIMessage): string => {
  const textParts = message.parts.filter(isTextUIPart);
  if (textParts.length > 0) {
    return textParts.map(p => p.text).join("");
  }
  const toolParts = message.parts.filter(isToolUIPart);
  if (toolParts.length > 0) {
    return toolParts.map(tool => `Calling ${getToolName(tool)}`).join(", ");
  }
  return "";
};
const ChatBox: React.FC<ChatBoxProps> = t0 => {
  const $ = _c(78);
  const {
    className,
    position: t1,
    onClose,
    showCloseButton: t2,
    showHeader: t3,
    showFeedback: t4,
    autoFocus: t5,
    aiProps,
    onToolCall
  } = t0;
  const position = t1 === undefined ? "fixed" : t1;
  const showCloseButton = t2 === undefined ? true : t2;
  const showHeader = t3 === undefined ? true : t3;
  const showFeedback = t4 === undefined ? true : t4;
  const autoFocus = t5 === undefined ? true : t5;
  const {
    data: userData
  } = useUserData();
  const messagesEndRef = useRef(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  let t6;
  if ($[0] !== aiProps.systemMessage) {
    t6 = aiProps.systemMessage ? [{
      id: "system",
      role: "system" as const,
      parts: [{
        type: "text" as const,
        text: aiProps.systemMessage
      }]
    }] : [];
    $[0] = aiProps.systemMessage;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      id: "initial",
      role: "assistant" as const,
      parts: [{
        type: "text" as const,
        text: "Hello! How can I help you today?"
      }]
    };
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] !== t6) {
    t8 = [...t6, t7];
    $[3] = t6;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const initialMessages = t8;
  let t9;
  if ($[5] !== aiProps.apiEndpoint) {
    t9 = new DefaultChatTransport({
      api: aiProps.apiEndpoint
    });
    $[5] = aiProps.apiEndpoint;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== onToolCall) {
    t10 = t11 => {
      const {
        toolCall
      } = t11;
      onToolCall({
        toolCallId: toolCall.toolCallId,
        toolName: toolCall.toolName,
        args: toolCall.input
      });
    };
    $[7] = onToolCall;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== initialMessages || $[10] !== t10 || $[11] !== t9) {
    t11 = {
      transport: t9,
      messages: initialMessages,
      onToolCall: t10,
      onError: _temp
    };
    $[9] = initialMessages;
    $[10] = t10;
    $[11] = t9;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const {
    messages,
    sendMessage,
    status
  } = useChat(t11);
  const isLoading = status === "streaming" || status === "submitted";
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      onSuccess: data => {
        setFeedbackSubmitted(true);
        showMutationToast(data);
      }
    };
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const {
    mutate: submitFeedback,
    isPending: isSubmittingFeedback
  } = api.misc.reviewSupportWithAI.useMutation(t12);
  let t13;
  if ($[14] !== aiProps.apiEndpoint || $[15] !== feedbackSubmitted || $[16] !== messages || $[17] !== submitFeedback) {
    t13 = sentiment => {
      if (feedbackSubmitted) {
        return;
      }
      submitFeedback({
        apiRoute: aiProps.apiEndpoint,
        chatHistory: messages,
        sentiment
      });
    };
    $[14] = aiProps.apiEndpoint;
    $[15] = feedbackSubmitted;
    $[16] = messages;
    $[17] = submitFeedback;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const handleFeedback = t13;
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const scrollToBottom = t14;
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      scrollToBottom();
    };
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== messages) {
    t16 = [messages];
    $[21] = messages;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  useEffect(t15, t16);
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      resolver: zodResolver(chatMessageSchema),
      defaultValues: {
        message: ""
      }
    };
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const form = useForm(t17);
  let t18;
  if ($[24] !== form || $[25] !== sendMessage) {
    t18 = form.handleSubmit(data_0 => {
      sendMessage({
        text: data_0.message
      });
      form.setValue("message", "");
    });
    $[24] = form;
    $[25] = sendMessage;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const handleSubmit = t18;
  if (!userData) {
    let t19;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <p>Must be logged in to use chat</p>;
      $[27] = t19;
    } else {
      t19 = $[27];
    }
    return t19;
  }
  const messagesContainerClass = showFeedback && messages.length > 2 ? "min-h-[200px] max-h-[500px]" : "min-h-96 max-h-[700px]";
  const t19 = position === "fixed" ? "fixed right-4 bottom-28 z-50 min-w-96 max-w-96 shadow-lg" : "w-full";
  let t20;
  if ($[28] !== className || $[29] !== t19) {
    t20 = cn(t19, "overflow-hidden rounded-md bg-popover", className);
    $[28] = className;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== onClose || $[32] !== showCloseButton || $[33] !== showHeader) {
    t21 = showHeader && <header className="flex items-center justify-between border-b px-4 py-2"><h4 className="font-medium text-lg">Chat</h4>{showCloseButton && <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /><span className="sr-only">Close chat window</span></Button>}</header>;
    $[31] = onClose;
    $[32] = showCloseButton;
    $[33] = showHeader;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== messagesContainerClass) {
    t22 = cn("flex-1 overflow-y-auto bg-card", messagesContainerClass);
    $[35] = messagesContainerClass;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== messages || $[38] !== userData) {
    let t24;
    if ($[40] !== userData) {
      t24 = (message_1, i) => {
        const content = getMessageText(message_1);
        const isUser = message_1.role === "user";
        return <div className={cn("flex flex-row items-start space-x-2 p-4", i % 2 === 0 ? "bg-card" : "bg-popover")} key={message_1.id}><div className="shrink-0">{isUser ? <AvatarImage href={userData.avatar} alt={userData.username} className="h-10 w-10 border-0" size={100} hover_effect={true} priority={true} /> : <BrainCircuit className="h-10 w-10" />}</div><div className="flex-1 space-y-1"><div className="flex items-center justify-between"><div className="font-medium text-sm">{isUser ? userData.username : "Seichi AI"}</div><div className="text-xs">{new Date().toLocaleTimeString()}</div></div><p className="text-sm">{content}</p></div></div>;
      };
      $[40] = userData;
      $[41] = t24;
    } else {
      t24 = $[41];
    }
    t23 = messages.filter(_temp2).map(t24);
    $[37] = messages;
    $[38] = userData;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[42] !== isLoading || $[43] !== messages.length) {
    t24 = isLoading && <div className={cn("flex flex-row items-start space-x-2 p-4", messages.length % 2 === 0 ? "bg-card" : "bg-popover")}><div className="shrink-0"><BrainCircuit className="h-10 w-10" /></div><div className="flex-1 space-y-1"><div className="flex items-center justify-between"><div className="font-medium text-sm">Seichi AI</div><div className="text-xs">{new Date().toLocaleTimeString()}</div></div><div className="flex flex-row items-center gap-2"><p className="text-sm">Thinking</p><div className="h-1 w-1 animate-bounce rounded-full bg-black [animation-delay:-0.3s] dark:bg-white" /><div className="h-1 w-1 animate-bounce rounded-full bg-black [animation-delay:-0.15s] dark:bg-white" /><div className="h-1 w-1 animate-bounce rounded-full bg-black dark:bg-white" /></div></div></div>;
    $[42] = isLoading;
    $[43] = messages.length;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div ref={messagesEndRef} />;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t22 || $[47] !== t23 || $[48] !== t24) {
    t26 = <div className={t22}>{t23}{t24}{t25}</div>;
    $[46] = t22;
    $[47] = t23;
    $[48] = t24;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== autoFocus || $[51] !== form.control || $[52] !== form.formState.errors.message?.message || $[53] !== handleSubmit || $[54] !== isLoading) {
    t27 = () => <FormItem><FormControl><RichInput id="message" height="120" control={form.control} disabled={isLoading} onSubmit={handleSubmit} error={form.formState.errors.message?.message} autoFocus={autoFocus} /></FormControl><FormMessage /></FormItem>;
    $[50] = autoFocus;
    $[51] = form.control;
    $[52] = form.formState.errors.message?.message;
    $[53] = handleSubmit;
    $[54] = isLoading;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== form.control || $[57] !== t27) {
    t28 = <FormField control={form.control} name="message" render={t27} />;
    $[56] = form.control;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== form || $[60] !== t28) {
    t29 = <Form {...form}>{t28}</Form>;
    $[59] = form;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== feedbackSubmitted || $[63] !== handleFeedback || $[64] !== isSubmittingFeedback || $[65] !== messages.length || $[66] !== showFeedback) {
    t30 = showFeedback && messages.length > 2 && <div className="mt-2 flex flex-col items-center"><p className="mb-1 text-muted-foreground text-xs">{feedbackSubmitted ? "Thank you for your feedback!" : "How was your chat experience?"}</p>{!feedbackSubmitted && <div className="flex space-x-2"><Button variant="outline" size="sm" onClick={() => handleFeedback("POSITIVE")} disabled={isSubmittingFeedback} className="flex h-8 items-center gap-1 px-2 py-1"><ThumbsUp className="h-3 w-3" /><span className="text-xs">Good</span></Button><Button variant="outline" size="sm" onClick={() => handleFeedback("NEUTRAL")} disabled={isSubmittingFeedback} className="flex h-8 items-center gap-1 px-2 py-1"><Meh className="h-3 w-3" /><span className="text-xs">Neutral</span></Button><Button variant="outline" size="sm" onClick={() => handleFeedback("NEGATIVE")} disabled={isSubmittingFeedback} className="flex h-8 items-center gap-1 px-2 py-1"><ThumbsDown className="h-3 w-3" /><span className="text-xs">Poor</span></Button></div>}</div>;
    $[62] = feedbackSubmitted;
    $[63] = handleFeedback;
    $[64] = isSubmittingFeedback;
    $[65] = messages.length;
    $[66] = showFeedback;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== t29 || $[69] !== t30) {
    t31 = <div className="border-t p-4">{t29}{t30}</div>;
    $[68] = t29;
    $[69] = t30;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== t21 || $[72] !== t26 || $[73] !== t31) {
    t32 = <div className="flex h-full flex-col">{t21}{t26}{t31}</div>;
    $[71] = t21;
    $[72] = t26;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t20 || $[76] !== t32) {
    t33 = <div className={t20}>{t32}</div>;
    $[75] = t20;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  return t33;
};
export default ChatBox;
function _temp(error) {
  const message = error?.message || "Error sending message. Not allowed?";
  showMutationToast({
    success: false,
    message
  });
}
function _temp2(message_0) {
  return message_0.role !== "system";
}